import {User, Briefcase, Bot, Settings} from 'lucide-react'
import {cn} from '@/lib/utils'
import {useI18n} from '@/i18n'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {TEMPLATES} from '../types'
import type {LucideIcon} from 'lucide-react'

interface TemplateSelectorProps {
    selectedKey: string
    onSelect: (key: string) => void
}

const TEMPLATE_ICONS: Record<string, LucideIcon> = {
    employee: User,
    contractor: Briefcase,
    service: Bot,
    custom: Settings,
}

/**
 * Horizontal card group for selecting onboarding templates.
 */
export function TemplateSelector({selectedKey, onSelect}: TemplateSelectorProps) {
    const i18n = useI18n()

    const templateLabels: Record<string, {title: string; desc: string}> = {
        employee: {title: i18n.onboarding.templates.employee, desc: i18n.onboarding.templates.employeeDesc},
        contractor: {title: i18n.onboarding.templates.contractor, desc: i18n.onboarding.templates.contractorDesc},
        service: {title: i18n.onboarding.templates.service, desc: i18n.onboarding.templates.serviceDesc},
        custom: {title: i18n.onboarding.templates.custom, desc: i18n.onboarding.templates.customDesc},
    }

    return (
        <Stack gap="sm">
            <Strong>{i18n.onboarding.templates.title}</Strong>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {TEMPLATES.map(template => {
                    const Icon = TEMPLATE_ICONS[template.key] ?? Settings
                    const labels = templateLabels[template.key]
                    const isSelected = selectedKey === template.key

                    return (
                        <button
                            key={template.key}
                            type="button"
                            onClick={() => onSelect(template.key)}
                            className={cn(
                                'flex flex-col items-center gap-2 rounded-lg border p-4 text-center transition-colors hover:bg-muted/50',
                                isSelected
                                    ? 'border-primary bg-primary/5 ring-2 ring-primary'
                                    : 'border-border'
                            )}
                            data-testid={`template-${template.key}`}
                        >
                            <Icon className={cn(
                                'h-6 w-6',
                                isSelected ? 'text-primary' : 'text-muted-foreground'
                            )}/>
                            <div>
                                <Strong>{labels?.title}</Strong>
                                <div className="text-xs text-muted-foreground">{labels?.desc}</div>
                            </div>
                        </button>
                    )
                })}
            </div>
        </Stack>
    )
}
